import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FluentProvider } from "@fluentui/react-components";
import { ThemeProvider } from "styled-components";
import { buildBrandingThemes } from "./branding/themeBranding.js";
import {
  chooseEffectiveAppearance,
  getUrlAppearanceOverride,
  readLocalAppearancePreference,
  writeLocalAppearancePreference
} from "./branding/appearancePreference.js";

const APPEARANCE_PREFERENCES = ["system", "dark", "light"];
const DEFAULT_ALLOWED_APPEARANCES = ["dark"];

export const AppearancePreferenceContext = createContext({
  appearancePreference: "system",
  allowedAppearances: DEFAULT_ALLOWED_APPEARANCES,
  setAppearancePreference: () => {}
});

export function useAppearancePreference() {
  return useContext(AppearancePreferenceContext);
}

function getSystemAppearance() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getAuthenticatedAppearance(api) {
  const appearance = api.getAuthUser()?.preferences?.appearance;
  if (!APPEARANCE_PREFERENCES.includes(appearance)) return null;
  writeLocalAppearancePreference(appearance);
  return appearance;
}

function getPreferredAppearance(api) {
  return readLocalAppearancePreference() || getAuthenticatedAppearance(api) || "system";
}

export default function BrandingThemeProvider({ api, children }) {
  const [siteSettings, setSiteSettings] = useState(null);
  const [preference, setPreference] = useState(() => getPreferredAppearance(api));
  const [systemAppearance, setSystemAppearance] = useState(getSystemAppearance);
  const allowedAppearances = siteSettings?.theme?.effectiveConfig?.appearance?.allowed || DEFAULT_ALLOWED_APPEARANCES;

  const setAppearancePreference = useCallback(
    nextPreference => {
      if (!APPEARANCE_PREFERENCES.includes(nextPreference)) return;

      writeLocalAppearancePreference(nextPreference);
      setPreference(nextPreference);

      const authenticatedUser = api.getAuthUser();
      if (!authenticatedUser || typeof api.updatePreferences !== "function") return;

      api.updatePreferences({ ...authenticatedUser.preferences, appearance: nextPreference }).catch(error => {
        console.error("Error saving appearance preference:", error);
      });
    },
    [api]
  );

  useEffect(() => {
    let active = true;
    const siteSettingsPromise = api.getPlatformSiteSettings();

    siteSettingsPromise
      .then(settings => {
        if (active) {
          setSiteSettings(settings);
        }
      })
      .catch(error => {
        console.error("Error fetching branding theme settings:", error);
      });

    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    const syncAuthenticatedAppearance = () => setPreference(getPreferredAppearance(api));

    syncAuthenticatedAppearance();
    api.addListener("authentication-changed", syncAuthenticatedAppearance);

    return () => {
      api.removeListener("authentication-changed", syncAuthenticatedAppearance);
    };
  }, [api]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const syncSystemAppearance = event => setSystemAppearance(event.matches ? "dark" : "light");

    setSystemAppearance(mediaQuery.matches ? "dark" : "light");
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncSystemAppearance);
      return () => mediaQuery.removeEventListener("change", syncSystemAppearance);
    }

    mediaQuery.addListener(syncSystemAppearance);
    return () => mediaQuery.removeListener(syncSystemAppearance);
  }, []);

  const effectiveAppearance = useMemo(() => {
    const theme = siteSettings?.theme;
    const appearanceOverride = getUrlAppearanceOverride(
      typeof window === "undefined" ? "" : window.location.search,
      allowedAppearances
    );

    if (appearanceOverride) return appearanceOverride;

    return chooseEffectiveAppearance({
      preference,
      systemAppearance,
      allowed: allowedAppearances,
      defaultAppearance: theme?.effectiveConfig?.appearance?.default
    });
  }, [allowedAppearances, preference, siteSettings, systemAppearance]);

  const { styledTheme, fluentTheme } = useMemo(
    () => buildBrandingThemes(siteSettings, effectiveAppearance),
    [effectiveAppearance, siteSettings]
  );

  const appearanceContextValue = useMemo(
    () => ({ appearancePreference: preference, allowedAppearances, setAppearancePreference }),
    [allowedAppearances, preference, setAppearancePreference]
  );

  return (
    <FluentProvider
      data-theme={effectiveAppearance}
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--primaryColorBackground)"
      }}
      theme={fluentTheme}
    >
      <AppearancePreferenceContext.Provider value={appearanceContextValue}>
        <ThemeProvider theme={styledTheme}>{children}</ThemeProvider>
      </AppearancePreferenceContext.Provider>
    </FluentProvider>
  );
}

BrandingThemeProvider.propTypes = {
  api: PropTypes.object.isRequired,
  children: PropTypes.node
};
